import { ref, reactive } from 'vue'

// Global image cache
const imageCache = reactive(new Map())
const loadingImages = reactive(new Set())
const failedImages = reactive(new Set())

// Cache statistics
const cacheStats = reactive({
    hits: 0,
    misses: 0,
    errors: 0,
    totalSize: 0
})

// Configuration
const config = {
    maxCacheSize: 50, // Maximum number of images to cache
    preloadDistance: 3, // Number of images to preload ahead
    retryAttempts: 2,
    retryDelay: 1000
}

const resolveMediaUrl = (value) => {
    if (!value || typeof value !== 'string') {
        return null
    }

    if (value.startsWith('http') || value.startsWith('/')) {
        return value
    }

    return `/storage/${value}`
}

const resolveItemPhoto = (item) => {
    if (!item || typeof item !== 'object') {
        return null
    }

    return resolveMediaUrl(item.photo_url || item.photo || null)
}

export function useImageCache() {
    const isLoading = ref(false)
    const error = ref(null)

    // Check if image is cached
    const isCached = (src) => {
        return imageCache.has(src)
    }

    // Get cache hit ratio
    const getCacheHitRatio = () => {
        const total = cacheStats.hits + cacheStats.misses
        return total > 0 ? (cacheStats.hits / total * 100).toFixed(1) : 0
    }

    // Preload a single image
    const preloadImage = async (src, retries = config.retryAttempts) => {
        if (!src || isCached(src) || loadingImages.has(src) || failedImages.has(src)) {
            if (isCached(src)) {
                cacheStats.hits++
            }
            return Promise.resolve()
        }

        loadingImages.add(src)
        cacheStats.misses++

        return new Promise((resolve, reject) => {
            const img = new Image()
            
            const cleanup = () => {
                loadingImages.delete(src)
                img.onload = null
                img.onerror = null
            }

            img.onload = () => {
                cleanup()
                
                // Add to cache
                imageCache.set(src, {
                    url: src,
                    timestamp: Date.now(),
                    size: img.naturalWidth * img.naturalHeight
                })
                
                // Update cache stats
                cacheStats.totalSize += img.naturalWidth * img.naturalHeight
                
                // Clean cache if it's too large
                cleanCache()
                
                resolve()
            }

            img.onerror = () => {
                cleanup()
                cacheStats.errors++
                
                if (retries > 0) {
                    // Retry after delay
                    setTimeout(() => {
                        preloadImage(src, retries - 1).then(resolve).catch(reject)
                    }, config.retryDelay)
                } else {
                    failedImages.add(src)
                    reject(new Error(`Failed to load image: ${src}`))
                }
            }

            img.src = src
        })
    }

    // Preload multiple images
    const preloadImages = async (sources) => {
        const promises = sources
            .filter(src => src && !isCached(src) && !loadingImages.has(src))
            .map(src => preloadImage(src).catch(() => {})) // Ignore individual failures
        
        return Promise.allSettled(promises)
    }

    // Preload images for current page and next few items
    const preloadPageImages = async (items, currentIndex = 0) => {
        const startIndex = Math.max(0, currentIndex - 1)
        const endIndex = Math.min(items.length, currentIndex + config.preloadDistance + 1)
        
        const imagesToPreload = items
            .slice(startIndex, endIndex)
            .map(resolveItemPhoto)
            .filter(Boolean)

        return preloadImages(imagesToPreload)
    }

    // Clean cache when it gets too large
    const cleanCache = () => {
        if (imageCache.size <= config.maxCacheSize) return

        // Convert to array and sort by timestamp (oldest first)
        const cacheEntries = Array.from(imageCache.entries())
            .sort((a, b) => a[1].timestamp - b[1].timestamp)

        // Remove oldest entries
        const toRemove = cacheEntries.slice(0, imageCache.size - config.maxCacheSize)
        toRemove.forEach(([key, value]) => {
            imageCache.delete(key)
            cacheStats.totalSize -= value.size
        })
    }

    // Clear all caches
    const clearCache = () => {
        imageCache.clear()
        loadingImages.clear()
        failedImages.clear()
        cacheStats.hits = 0
        cacheStats.misses = 0
        cacheStats.errors = 0
        cacheStats.totalSize = 0
    }

    // Get cache information
    const getCacheInfo = () => {
        return {
            size: imageCache.size,
            maxSize: config.maxCacheSize,
            hitRatio: getCacheHitRatio(),
            stats: { ...cacheStats },
            loadingCount: loadingImages.size,
            failedCount: failedImages.size
        }
    }

    // Retry failed images
    const retryFailedImages = async () => {
        const failedSources = Array.from(failedImages)
        failedImages.clear()
        return preloadImages(failedSources)
    }

    // Smart preloading based on user behavior
    const smartPreload = async (items, userScrollDirection = 'down') => {
        if (!items || items.length === 0) return

        // Preload based on scroll direction
        const preloadCount = userScrollDirection === 'down' ? 
            Math.min(5, items.length) : 
            Math.min(3, items.length)

        const imagesToPreload = items
            .slice(0, preloadCount)
            .map(resolveItemPhoto)
            .filter(Boolean)

        return preloadImages(imagesToPreload)
    }

    return {
        // State
        isLoading,
        error,
        
        // Methods
        preloadImage,
        preloadImages,
        preloadPageImages,
        smartPreload,
        isCached,
        clearCache,
        retryFailedImages,
        
        // Cache info
        getCacheInfo,
        getCacheHitRatio,
        
        // Cache objects (readonly)
        cache: imageCache,
        loadingImages,
        failedImages,
        cacheStats
    }
}

// Global instance for sharing across components
export const globalImageCache = useImageCache()
